import { useEffect, useRef, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useOfflineDetection } from './useServiceWorker'
import toast from 'react-hot-toast'

interface SessionManagerConfig {
  // Tempo em ms para considerar sessão obsoleta (padrão: 30 minutos)
  staleSessionThreshold: number
  // Tempo em ms para considerar dados obsoletos após visibilidade (padrão: 5 minutos)
  staleDataThreshold: number
  // Queries que devem ser invalidadas em reconexão
  criticalQueries: string[]
  // Habilitar logs de debug
  debug: boolean
  // Período de graça após inicialização da app (padrão: 5 segundos)
  gracePeriod: number
}

const DEFAULT_CONFIG: SessionManagerConfig = {
  staleSessionThreshold: 30 * 60 * 1000, // 30 minutos
  staleDataThreshold: 5 * 60 * 1000, // 5 minutos
  gracePeriod: 5 * 1000, // 5 segundos
  criticalQueries: [
    'questions',
    'adminStats',
    'systemSettings',
    'userProfile',
    'subscriptions',
    'assessments'
  ],
  debug: false
}

/**
 * Hook para gerenciar sessão e reconexão automática
 * 
 * Detecta e resolve automaticamente:
 * - Hibernação/retomada do sistema
 * - Navegação prolongada entre páginas
 * - Perda e recuperação de conexão
 * - Dados obsoletos após inatividade
 */
export const useSessionManager = (config: Partial<SessionManagerConfig> = {}) => {
  const finalConfig = { ...DEFAULT_CONFIG, ...config }
  const queryClient = useQueryClient()
  const { isOffline, wasOffline } = useOfflineDetection()

  // Refs para rastrear estado
  const lastActivityRef = useRef<number>(Date.now())
  const lastVisibilityChangeRef = useRef<number>(Date.now())
  const pageHiddenTimeRef = useRef<number | null>(null)
  const isInitializedRef = useRef<boolean>(false)
  const appStartTimeRef = useRef<number>(Date.now())
  const gracePeriodActiveRef = useRef<boolean>(true)
  
  const log = useCallback((_message: string, ..._args: any[]) => {
    // Debug logging disabled
  }, [])

  /**
   * Verifica se ainda estamos no período de graça após inicialização
   */
  const isInGracePeriod = useCallback(() => {
    const timeSinceStart = Date.now() - appStartTimeRef.current
    const inGracePeriod = timeSinceStart < finalConfig.gracePeriod

    if (inGracePeriod && !gracePeriodActiveRef.current) {
      gracePeriodActiveRef.current = true
    } else if (!inGracePeriod && gracePeriodActiveRef.current) {
      gracePeriodActiveRef.current = false
      log('Grace period ended, session manager fully active')
    }

    return inGracePeriod
  }, [finalConfig.gracePeriod, log])

  /**
   * Invalida queries críticas (respeitando período de graça)
   */
  const invalidateCriticalQueries = useCallback(async (reason: string) => {
    // Durante o período de graça, apenas log mas não invalida queries
    if (isInGracePeriod()) {
      log(`Skipping query invalidation during grace period - Reason: ${reason}`)
      return
    }

    log(`Invalidating critical queries - Reason: ${reason}`)

    try {
      // Invalidar queries específicas de forma mais seletiva
      for (const queryKey of finalConfig.criticalQueries) {
        await queryClient.invalidateQueries({
          queryKey: [queryKey],
          exact: false // Invalida todas as variações da query
        })
      }

      // Remover apenas queries muito antigas do cache (mais conservador)
      queryClient.removeQueries({
        predicate: (query) => {
          const isVeryOld = Date.now() - query.state.dataUpdatedAt > (finalConfig.staleDataThreshold * 2)
          return isVeryOld
        }
      })

      // Forçar refetch de queries ativas que estão stale (apenas se não estiver em grace period)
      await queryClient.refetchQueries({
        type: 'active',
        stale: true,
        exact: false
      })

      log(`Successfully invalidated ${finalConfig.criticalQueries.length} query types`)

      // Mostrar notificação discreta apenas se não for inicialização
      if (!reason.includes('initialization') && !reason.includes('grace')) {
        toast.success('Dados atualizados automaticamente', {
          duration: 2000,
          position: 'bottom-right'
        })
      }

    } catch (error) {
      // Error handled silently
    }
  }, [queryClient, finalConfig.criticalQueries, finalConfig.staleDataThreshold, log, isInGracePeriod])

  /**
   * Verifica se a sessão está obsoleta
   */
  const checkStaleSession = useCallback(() => {
    const now = Date.now()
    const timeSinceLastActivity = now - lastActivityRef.current
    
    log(`Checking stale session - Time since last activity: ${timeSinceLastActivity}ms`)
    
    return timeSinceLastActivity > finalConfig.staleSessionThreshold
  }, [finalConfig.staleSessionThreshold, log])

  /**
   * Verifica se os dados estão obsoletos após mudança de visibilidade
   */
  const checkStaleDataAfterVisibility = useCallback(() => {
    if (!pageHiddenTimeRef.current) return false
    
    const now = Date.now()
    const timeHidden = now - pageHiddenTimeRef.current
    
    log(`Checking stale data after visibility - Time hidden: ${timeHidden}ms`)
    
    return timeHidden > finalConfig.staleDataThreshold
  }, [finalConfig.staleDataThreshold, log])

  /**
   * Atualiza timestamp da última atividade
   */
  const updateLastActivity = useCallback(() => {
    lastActivityRef.current = Date.now()
    log('Updated last activity timestamp')
  }, [log])

  /**
   * Detecta queries em estado de loading infinito
   */
  const detectStuckQueries = useCallback(() => {
    // Durante o período de graça, não detectar queries stuck
    if (isInGracePeriod()) {
      return []
    }

    const cache = queryClient.getQueryCache()
    const queries = cache.getAll()
    const stuckQueries = []

    for (const query of queries) {
      const state = query.state as any // Type assertion to access isFetching
      const isFetching = state.isFetching
      const dataUpdatedAt = state.dataUpdatedAt
      const now = Date.now()

      // Se está fazendo fetch há mais de 45 segundos (aumentado de 30), considerar "stuck"
      // Usar dataUpdatedAt como proxy para quando o fetch começou
      if (isFetching && dataUpdatedAt && (now - dataUpdatedAt) > 45000) {
        stuckQueries.push(query)
        log(`Detected stuck query: ${query.queryHash}`)
      }
    }

    return stuckQueries
  }, [queryClient, log, isInGracePeriod])

  /**
   * Resolve queries em estado de loading infinito (respeitando período de graça)
   */
  const resolveStuckQueries = useCallback(async () => {
    // Durante o período de graça, não resolver queries stuck
    if (isInGracePeriod()) {
      log('Skipping stuck query resolution during grace period')
      return
    }

    const stuckQueries = detectStuckQueries()

    if (stuckQueries.length > 0) {
      log(`Resolving ${stuckQueries.length} stuck queries`)

      for (const query of stuckQueries) {
        try {
          // Cancelar fetch atual
          await queryClient.cancelQueries({ queryKey: query.queryKey })

          // Invalidar e refetch com delay maior para evitar race conditions
          setTimeout(async () => {
            await queryClient.invalidateQueries({
              queryKey: query.queryKey,
              exact: false
            })
          }, 1000)
        } catch (error) {
          log(`Error resolving stuck query ${query.queryHash}:`, error)
        }
      }

      // Forçar refetch após um delay maior
      setTimeout(() => {
        queryClient.refetchQueries({
          type: 'active',
          stale: true
        })
      }, 2000)
    }
  }, [detectStuckQueries, queryClient, log, isInGracePeriod])

  /**
   * Handler para mudança de visibilidade (respeitando período de graça)
   */
  const handleVisibilityChange = useCallback(() => {
    const now = Date.now()

    if (document.hidden) {
      // Página ficou oculta
      pageHiddenTimeRef.current = now
      log('Page hidden')
    } else {
      // Página ficou visível
      log('Page visible')

      // Durante o período de graça, apenas atualizar timestamps
      if (isInGracePeriod()) {
        log('Page visible during grace period, skipping stale checks')
        pageHiddenTimeRef.current = null
        lastVisibilityChangeRef.current = now
        updateLastActivity()
        return
      }

      const wasStale = checkStaleSession() || checkStaleDataAfterVisibility()

      if (wasStale) {
        log('Detected stale session/data, invalidating queries')
        invalidateCriticalQueries('Page visibility - stale data detected')
      }

      // Verificar e resolver queries "stuck" com delay maior
      setTimeout(() => {
        resolveStuckQueries()
      }, 2000)

      // Reset timestamps
      pageHiddenTimeRef.current = null
      lastVisibilityChangeRef.current = now
      updateLastActivity()
    }
  }, [checkStaleSession, checkStaleDataAfterVisibility, invalidateCriticalQueries, resolveStuckQueries, updateLastActivity, log, isInGracePeriod])

  /**
   * Handler para reconexão
   */
  const handleReconnection = useCallback(() => {
    if (wasOffline && !isOffline) {
      log('Reconnection detected, invalidating queries')
      invalidateCriticalQueries('Network reconnection')
      updateLastActivity()
    }
  }, [wasOffline, isOffline, invalidateCriticalQueries, updateLastActivity, log])

  /**
   * Handler para atividade do usuário
   */
  const handleUserActivity = useCallback(() => {
    updateLastActivity()
  }, [updateLastActivity])

  // Setup dos event listeners
  useEffect(() => {
    if (isInitializedRef.current) return
    
    log('Initializing session manager')
    
    // Listeners para visibilidade
    document.addEventListener('visibilitychange', handleVisibilityChange)
    
    // Listeners para atividade do usuário
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']
    activityEvents.forEach(event => {
      document.addEventListener(event, handleUserActivity, { passive: true })
    })
    
    // Listeners para foco da janela
    window.addEventListener('focus', handleVisibilityChange)
    window.addEventListener('blur', handleVisibilityChange)
    
    isInitializedRef.current = true
    updateLastActivity()
    
    return () => {
      log('Cleaning up session manager')
      
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleVisibilityChange)
      window.removeEventListener('blur', handleVisibilityChange)
      
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleUserActivity)
      })
    }
  }, [handleVisibilityChange, handleUserActivity, updateLastActivity, log])

  // Effect para reconexão
  useEffect(() => {
    handleReconnection()
  }, [handleReconnection])

  // Verificação periódica de sessão obsoleta e queries stuck (respeitando período de graça)
  useEffect(() => {
    const interval = setInterval(() => {
      // Não executar verificações durante o período de graça
      if (isInGracePeriod()) {
        return
      }

      if (!document.hidden) {
        // Verificar sessão obsoleta
        if (checkStaleSession()) {
          log('Periodic check detected stale session')
          invalidateCriticalQueries('Periodic check - stale session')
        }

        // Verificar queries stuck
        const stuckQueries = detectStuckQueries()
        if (stuckQueries.length > 0) {
          log('Periodic check detected stuck queries')
          resolveStuckQueries()
        }
      }
    }, 30000) // Verificar a cada 30 segundos

    return () => clearInterval(interval)
  }, [checkStaleSession, invalidateCriticalQueries, detectStuckQueries, resolveStuckQueries, log, isInGracePeriod])

  return {
    isOffline,
    wasOffline,
    lastActivity: lastActivityRef.current,
    invalidateCriticalQueries,
    updateLastActivity,
    checkStaleSession
  }
}
